import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUpcomingService, type UpcomingServiceItem } from '../../api';
import { useSettings, formatNumber } from '../../context/SettingsContext';
import { kmToDisplay, distanceUnitLabel } from '../../utils/units';
import { Card } from '../ui';

export default function UpcomingServiceWidget() {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const distUnit = settings.distanceUnit;
  const distLabel = distanceUnitLabel(distUnit);

  const [items, setItems] = useState<UpcomingServiceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    getUpcomingService()
      .then(data => { setItems(data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  // Returnerer tekst + om rækken er overskredet (til farve)
  const describe = (item: UpcomingServiceItem): { text: string; overdue: boolean } => {
    if (item.kind === 'km') {
      const km = item.km_remaining ?? 0;
      if (km <= 0) {
        const over = formatNumber(kmToDisplay(Math.abs(km), distUnit) ?? 0, settings.numberFormat, 0);
        return { text: t('dashboard.service_km_overdue', { km: over, unit: distLabel }), overdue: true };
      }
      const val = formatNumber(kmToDisplay(km, distUnit) ?? 0, settings.numberFormat, 0);
      return { text: t('dashboard.service_km_left', { km: val, unit: distLabel }), overdue: false };
    }
    const days = item.days_remaining ?? 0;
    if (days < 0)  return { text: t('dashboard.service_days_overdue', { count: -days }), overdue: true };
    if (days === 0) return { text: t('dashboard.service_today'), overdue: true };
    return { text: t('dashboard.service_days_left', { count: days }), overdue: false };
  };

  return (
    <Card padded className="h-full">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-base font-semibold text-ink-900 tracking-tight">
          {t('dashboard.upcoming_service_title', { defaultValue: 'Kommende service' })}
        </h3>
        {items.length > 0 && (
          <span className="text-xs text-ink-500">{items.length}</span>
        )}
      </div>

      {loading && <p className="text-sm text-ink-500 mt-4">…</p>}
      {error && <p className="text-sm text-rose-700 mt-4">{t('dashboard.load_error', { defaultValue: 'Kunne ikke hente data' })}</p>}
      {!loading && !error && items.length === 0 && (
        <p className="text-sm text-ink-500 mt-4">{t('dashboard.upcoming_service_empty', { defaultValue: 'Ingen biler nærmer sig service' })}</p>
      )}

      <div className="mt-3 -mx-1">
        {items.map(item => {
          const { text, overdue } = describe(item);
          return (
            <Link
              key={`${item.kind}-${item.vehicle_id}`}
              to={`/vehicles/${item.vehicle_id}/history`}
              className="w-full text-left flex items-center justify-between gap-3 px-[15px] py-2.5 border-b border-cream-100 last:border-b-0 hover:bg-cream-100 transition-colors"
            >
              <span className="text-sm font-medium text-ink-900 font-mono truncate">
                {item.registration_number}
              </span>
              <span className={`text-sm font-semibold shrink-0 ${overdue ? 'text-rose-700' : 'text-ink-900'}`}>
                {text}
              </span>
            </Link>
          );
        })}
      </div>
    </Card>
  );
}
